import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Container, Row, Button, Modal, ModalHeader, ModalBody, Nav, NavItem, NavLink } from "reactstrap";
import * as Yup from "yup";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import ErrorAlert from "../../components/Common/Error/ErrorAlert";
import { generatePresignedUrl, getAllBeatswithChargeGaurd, getAllGuardLocations, getAllVehicleLocation } from "../../store/common/actions";
import { updateTicket } from "../../store/ticket/actions";
import CommonForm from "../../components/Common/CommonForm";
import { createSelector } from "reselect";
import UpdateTicketMap from "../../components/Common/Map/updateTicketMap";

export default function ViewSelectedTicket({data}) {
  const [formConfig, setFormConfig] = useState({});
  const [selectedIncidentType, setSelectedIncidentType] = useState("");
  const [assignedUser, setAssignedUser] = useState(null); // State for assigned user
  const [storedIncidentsResponse, setStoredIncidentsResponse] = useState([]);
  const [formData, setFormData] = useState([]);
  const [formData2, setFormData2] = useState([]);
  const [formData3, setFormData3] = useState([]);

  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [showGuardLocations, setShowGuardLocations] = useState(false);
  const [showCarLocations, setShowCarLocations] = useState(false);
  const [showBeatLocations, setShowBeatLocations] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  var editable = false;

  const locationsSelector = createSelector(
    (state) => state.Common,
    (Common) => ({
      guardLocations: Common.location || [],
      vehicleLocations: Common.vehicleLocation || [],
      beatLocations: Common.beatsLocations || []
    })
  );

  const { guardLocations, vehicleLocations, beatLocations } = useSelector(locationsSelector);

  const { error = null } = useSelector((state) => state.ticketReducer || {});

  const initializeFormValues = (fields) =>
    fields.reduce((acc, field) => {
      acc[field.inputName] = field.type === "file" ? [] : "";
      return acc;
    }, {});

  useEffect(() => {
    const ticketData = data || {};
    setFormData(ticketData);
    setFormData2(ticketData);
    setFormData3(ticketData);

    const incidents = JSON.parse(localStorage.getItem("incidentTypes") || "[]");
    setStoredIncidentsResponse(incidents);

    if (ticketData.incidentType && incidents.length) {
      const matchedIncident = incidents.find(
        (incident) => incident.incidentTypeId === ticketData.incidentType
      );
      if (matchedIncident) {
        setSelectedIncidentType(matchedIncident.id);
      }
    }

    const storedFormResponse = JSON.parse(localStorage.getItem("formResponse"));
    if (storedFormResponse) {
      const ticketUpdationForm = storedFormResponse.find(
        (form) => form.formId === "ticket_updation_form"
      );
      const ticketCommonForm = storedFormResponse.find(
        (form) => form.formId === "ticket_common_form"
      );

      // Extract nested fields for selected incident type
      const incidentTypeFields =
        ticketUpdationForm?.fields[selectedIncidentType] || [];

      // Combine incident-specific fields and common ticket fields
      const combinedFields = [
        ...(Array.isArray(ticketCommonForm?.fields)
          ? ticketCommonForm.fields
          : []),
        ...incidentTypeFields,
      ];

      setFormConfig(combinedFields);
    }

    if (ticketData.incidentLocation) {
      handleMapLocationSelect({
        lat: ticketData.incidentLocation.lat,
        lng: ticketData.incidentLocation.long,
      });
    }
  }, [location.state, selectedIncidentType]);

  const handleMapLocationSelect = ({ lat, lng }) => {
    if (lat === undefined || lng === undefined) {
      console.warn("Invalid location data received:", { lat, lng });
      return;
    }

    const locationValue = `${lat}, ${lng}`; // Ensure it's a comma-separated value
    setFormData((prev) => ({
      ...prev,
      incidentLocation: locationValue, // Always store comma-separated value
      latitude: lat || 0,
      longitude: lng || 0,
    }));
  };

  const generateValidationSchema = (fields) => {
    return Yup.object(
      fields?.reduce((schema, field) => {
        if (field.required) {
          schema[field.inputName] = Yup.string().required(
            `${field.label} is required`
          );
        }
        return schema;
      }, {})
    );
  };
  const handleSubmit = async (values) => {
    try {
      // Fetch incident types from localStorage
      const incidentTypes = JSON.parse(
        localStorage.getItem("incidentTypes") || "[]"
      );

      // Find the corresponding incident type ID based on selectedIncidentType
      const selectedIncident = incidentTypes.find(
        (incident) => incident.id === selectedIncidentType
      );

      if (!selectedIncident) {
        console.error("Invalid or missing incident type in localStorage");
        return;
      }
      const incidentTypeId = selectedIncident.incidentTypeId;

      const audioFileFields = Object.keys(values).filter(
        (key) =>
          key.includes("audio") && // Check if the key contains "media"
          Array.isArray(values[key]) && 
          values[key]?.[0] instanceof File
      );

      const mediaFileFields = Object.keys(values).filter(
        (key) =>
          key.includes("media") && // Check if the key contains "media"
          Array.isArray(values[key]) && 
          values[key]?.[0] instanceof File
      );
      
      const media = [];
      const audio = [];


      // Wait for all files to be uploaded
      await Promise.all(
        audioFileFields?.flatMap((key) =>
          Array.from(values[key])?.map(async (file) => {
            const fileExtension = file.name.split(".").pop();
            const timestamp = Date.now();
            const uniqueFileName = `${timestamp}.${fileExtension}`;
            const filePath = `ticket/media/${uniqueFileName}`;
            const response = await dispatch(
              generatePresignedUrl({ fileName: filePath, file })
            );
            if (response?.payload?.fileName) {
              audio.push(filePath);
            }
          })
        )
      );
      // Wait for all files to be uploaded
      await Promise.all(
        mediaFileFields.flatMap((key) =>
          Array.from(values[key])?.map(async (file) => {
            const fileExtension = file.name.split(".").pop();
            const timestamp = Date.now();
            const uniqueFileName = `${timestamp}.${fileExtension}`;
            const filePath = `ticket/media/${uniqueFileName}`;
            const response = await dispatch(
              generatePresignedUrl({ fileName: filePath, file })
            );
            if (response?.payload?.fileName) {
              media.push(filePath);
            }
          })
        )
      );

      // Construct the payload
      const payload = {
        ...values,
        incidentLocation: {
          lat: values.latitude, // Ensure latitude and longitude are taken from values
          long: values.longitude,
        },
        media: [...(formData2.media??[]), ...media], // Add the media array to the payload
        audio: [ ...audio,...(formData3?.audio ?? [])],
        incidentTypeId,
      };
      dispatch(updateTicket(payload, navigate));
      // Remove unnecessary fields
      delete payload.latitude;
      delete payload.longitude;
      mediaFileFields.forEach((key) => delete payload[key]);
      audioFileFields.forEach((key) => delete payload[key]);


      // Dispatch the updateTicket action with payload
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const toggleMapModal = () => {
    setIsMapModalOpen(!isMapModalOpen);
  };

  // Add new function to handle API calls
  const fetchLocationData = async (type) => {
    try {
      switch(type) {
        case 'guards':
          dispatch(getAllGuardLocations());
          break;
        case 'cars':
          dispatch(getAllVehicleLocation());
          break;
        case 'beats':
          dispatch(getAllBeatswithChargeGaurd());
          break;
        default:
          return;
      }
    } catch (error) {
      console.error(`Error fetching ${type} locations:`, error);
    }
  };

  // Simplified checkbox handlers
  const handleGuardLocationsChange = (e) => {
    setShowGuardLocations(e);
    if (e) {
      fetchLocationData('guards');
    }
  };

  const handleCarLocationsChange = (e) => {
    setShowCarLocations(e);
    if (e) {
      fetchLocationData('cars');
    }
  };

  const handleBeatLocationsChange = (e) => {
    setShowBeatLocations(e);
    if (e) {
      fetchLocationData('beats');
    }
  };

  return (
    <React.Fragment>
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                    <label htmlFor="incidentType">
                      घटनेचा प्रकार (Type of Incident)
                    </label>
                    <Row className="mb-3">
                    {/* <Col md={10}> */}
                    <select
                      id="incidentType"
                      disabled
                      className="form-control"
                      value={selectedIncidentType}
                      onChange={(e) => setSelectedIncidentType(e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {storedIncidentsResponse &&
                        storedIncidentsResponse.map((incident) => (
                          <option key={incident.id} value={incident.id}>
                            {incident.incidentName}
                          </option>
                        ))}
                    </select>
                    {/* </Col> */}
                    
                  </Row>

                  {selectedIncidentType && (
                    <>
                    
                      <CommonForm
                        formConfig={formConfig}
                        initialValues={formData}
                        setFormData={setFormData}
                        validationSchema={generateValidationSchema(
                          formConfig[selectedIncidentType] || []
                        )}
                        disabled={!editable}
                        onSubmit={handleSubmit}
                        submitText="Update Ticket"
                        setAssignedUser={setAssignedUser}
                        assignedUser={assignedUser}
                      />
                    </>
                  )}
                  <br />
                  <ErrorAlert error={error} />
                </CardBody>
              </Card>
            </Col>
          </Row>

         
    </React.Fragment>
  );
}
